import { mapGetters, mapActions, mapMutations } from 'vuex';
import { MediaMixin, SortEnum } from '@loandolphin/dolphin-framework';
import { authComputed } from '@state/helpers';
import getSortingHeaderData from '@utils/offer/get-sorting-header-data';
// import { SortModeEnum, SortFieldEnum, isAutomaticMode } from '@utils/offer/sort-offers';
import { SortModeEnum } from '@utils/offer/sort-offers';
import { groupProviders, GroupEnum } from '@utils/enquiry/group-providers';
import { trackOfferExpanded, trackOfferEnquired, trackOfferAccepted } from '@utils/analytics/events';
import getOfferData from '@utils/offer/get-offer-data';
import triggerEvent from '@utils/google-tag/trigger';
import StateEnum, { ApiStateEnum, isClosedState } from '@enums/state-enum';
import getState from '@utils/get-state';
import { isExpired } from '@enums/offer-state-enum';
import EnquirySettings from '@components/dashboard-enquiry/enquiry-settings';
import EnquireModal from '@components/dashboard-enquiry/enquire-modal';
import ContactModal from '@components/dashboard-enquiry/contact-modal';
import rankOffers from '@utils/offer/rank-offers';
import getDate from '@utils/date/get-date';

export default {
  name: 'dashboard-enquiry',
  props: ['uuid', 'statusData', 'overviewData'],
  mixins: [MediaMixin],
  components: {
    EnquirySettings,
    EnquireModal,
    ContactModal,
  },

  data() {
    return {
      showEnquireModal: false,
      showContactModal: false,
      enquireOfferUuid: null,
      sortingType: SortEnum.NONE,
      sortingKey: null,
      suspendRender: false,
      mobile: {
        isDetailsOpen: false,
        currentOfferUuid: null,
        offerData: null,
        commonCopy: null,
      },
    };
  },

  computed: {
    ...authComputed,

    ...mapGetters({
      getEnquiryByUuid: 'connector/getEnquiryByUuid',
      getOfferByUuid: 'connector/getOfferByUuid',
    }),

    enquiry() {
      return this.getEnquiryByUuid(this.uuid);
    },

    state() {
      return getState(this.enquiry);
    },

    offers() {
      rankOffers(this.enquiry.offers);

      return this.enquiry.offers;
    },

    commonGroupCopy() {
      return {
        title: this.$t('dashboard.offers.from'),
        rating: this.$t('dashboard.label.rating'),
      };
    },

    isClosedEnquiry() {
      return this.enquiry && isClosedState(this.state);
    },

    isCreditScorePending() {
      return this.enquiry && this.state === StateEnum.CREATED;
    },

    isCreditScoreFailed() {
      return this.enquiry && this.enquiry.status === ApiStateEnum.CREDIT_SCORE_FAILED;
    },

    headerData() {
      return getSortingHeaderData();
    },

    hasOffers() {
      return this.groupedOffers.length > 0;
    },

    showFilters() {
      // Hidding filters until this feature is released
      return false;
    },

    sortOptions() {
      // if (this.sortingType === SortEnum.NONE && isAutomaticMode(this.offers)) {
      //   return {
      //     mode: SortModeEnum.FIELD_SORT,
      //     field: SortFieldEnum.INTEREST_RATE,
      //     order: SortEnum.ASCENDING,
      //   };
      // }

      if (this.sortingType === SortEnum.NONE) {
        return { mode: SortModeEnum.DEFAULT };
      }

      return {
        mode: SortModeEnum.FIELD_SORT,
        field: this.sortingKey,
        order: this.sortingType,
      };
    },

    groupedOffers() {
      if (!this.enquiry) {
        return [];
      }

      const grouped = groupProviders(this.offers, this.sortOptions);
      const groupOrder = GroupEnum.values();

      const groupedOffers = groupOrder
        .map((groupType) => {
          const offers = grouped[groupType];
          if (!offers || offers.length === 0) {
            return null;
          }

          return {
            groupType,
            isCollapsible: true,
            isCollapsed: true,
            offersData: this.offersData(offers),
          };
        })
        .filter((i) => i); // Remove empty elements.

      if (groupedOffers.length > 0) {
        groupedOffers[0].showFilter = true;
        groupedOffers[0].isCollapsed = true;
        groupedOffers[0].isCollapsible = false;
      }

      return groupedOffers;
    },
  },

  methods: {
    ...mapActions({
      acceptOffer: 'connector/acceptOffer',
      listEnquiries: 'connector/listEnquiries',
      createContactPreferences: 'connector/createContactPreferences',
    }),

    ...mapMutations({
      updateOffer: 'connector/UPDATE_OFFER',
    }),

    offersData(offers) {
      return offers.map((offer) => getOfferData(this.enquiry, offer));
    },

    commonCopy(offer) {
      const actionLabel = isExpired(offer)
        ? this.$t('dashboard.offer.expired')
        : this.$t('dashboard.offer.enquire');

      return {
        action: actionLabel,
        extras: this.$t('dashboard.offer.extra_features'),
        stage: this.$t('dashboard.label.stage'),
        broker: this.$t('dashboard.label.broker'),
        details: this.$t('dashboard.updates.actions.details'),
        updates: this.$t('dashboard.updates.actions.updates'),
        less: this.$t('dashboard.updates.actions.less'),
        more: this.$t('dashboard.updates.actions.more'),
      };
    },

    trackOfferExpanded(offerUuid) {
      trackOfferExpanded(this.currentUser, this.enquiry, this.getOfferByUuid(offerUuid));
    },

    async initEnquireOffer(offerUuid) {
      trackOfferEnquired(this.currentUser, this.enquiry, this.getOfferByUuid(offerUuid));
      this.showEnquireModal = true;
      this.enquireOfferUuid = offerUuid;
      // the following logic is used to trigger a google tag manager event
      // this event will trigger running the A/B test that changes the text for the CTA
      // because this CTA button is in the confirm modal, it is re-called when the modal opens
      await this.$nextTick();

      return triggerEvent('ab.ccon.declare-button');
    },

    toggleCollapse(group) {
      group.isCollapsed = !group.isCollapsed;
      this.$forceUpdate();
    },

    getRefName(group) {
      return `collapsibleContent-${group.groupType}`;
    },

    getGroupHeight(group) {
      if (group.isCollapsed) {
        return 'auto';
      }

      return '0px';
    },

    confirmEnquireOffer() {
      const offer = this.getOfferByUuid(this.enquireOfferUuid);

      return this.acceptOffer(offer)
        .then(() => {
          trackOfferAccepted(this.currentUser, this.enquiry, offer);
          this.listEnquiries();
          this.showContactModal = true;
        });
    },

    /**
     * @param {Array} days
     * @param {String} start - date json
     * @param {String} end - date json
     * @returns
     */
    confirmEnquireContact(days, start, end) {
      const offer = this.getOfferByUuid(this.enquireOfferUuid);

      if (days.length === 0 || start === '' || end === '') {
        return Promise.resolve();
      }

      const startDate = getDate(start);
      const endDate = getDate(end);

      return this.createContactPreferences({ offer, days, startDate, endDate })
        .then(() => this.listEnquiries());
    },

    isExpiredGroup(group) {
      return group === GroupEnum.EXPIRED;
    },

    closeSettingsModal(event) {
      const settings = { ...this.statusData.settings, active: event };
      this.$emit('update:settings', settings);
    },

    expandedCallback(offerData, isExpanded) {
      if (!this.isBiggerThanLaptop) {
        this.trackOfferExpanded(offerData._offer.uuid);
        this.updateMobileOffer(offerData);

        return;
      }

      this.updateOffer({ uuid: offerData._offer.uuid, isExpanded });

      if (!isExpanded) {
        return;
      }

      this.trackOfferExpanded(offerData._offer.uuid);
    },

    mobileCommonCopy(offer) {
      const commonCopy = this.commonCopy(offer);

      return { ...commonCopy,
        viewProfile: this.$t('dashboard.action.mobile.view_profile'),
        broker: this.$t('dashboard.label.broker'),
        backToTop: this.$t('dashboard.action.mobile.back_to_top'),
        title: this.$t('dashboard.title.mobile.offer_details'),
        action: this.$t('dashboard.offer.enquire') };
    },

    updateMobileOffer(offerData) {
      this.mobile.currentOfferUuid = offerData._offer.uuid;
      this.mobile.commonCopy = this.mobileCommonCopy(this.getOfferByUuid(offerData._offer.uuid));
      this.mobile.offerData = offerData;
      this.mobile.isDetailsOpen = true;
    },
  },

  async mounted() {
    await this.$nextTick();
    this.$forceUpdate();
    fixSvgUrls();
    // the following logic is used to trigger a google tag manager event
    // this event will trigger running the A/B test that changes the text for the CTA
    // because this CTA button is in the confirm modal, it is re-called when the modal opens

    return triggerEvent('ab.ccon.declare-button');
  },
};

/**
 * Fixes references to SVG IDs.
 * Safari won't display SVG masks/fills e.g. referenced with
 * `url(#id)` when the <base> tag is on the page.
 *
 * More info:
 * - http://stackoverflow.com/a/18265336/796152
 * - http://www.w3.org/TR/SVG/linking.html
 */
export function fixSvgUrls() {
  function fixForAttribute(attrib) {
    const baseUrl = window.location.href;

    /**
     * Find all svg elements with the given attribute, e.g. for `mask`.
     * See: http://stackoverflow.com/a/23047888/796152
     */
    [].slice.call(document.querySelectorAll(`svg [${attrib}]`))
      // filter out all elements whose attribute doesn't start with `url(#`
      .filter((element) => element.getAttribute(attrib).indexOf('url(#') === 0)
      // prepend `window.location` to the attrib's url() value, in order to make it an absolute IRI
      .forEach((element) => {
        const maskId = element.getAttribute(attrib).replace('url(', '').replace(')', '');
        element.setAttribute(attrib, `url(${baseUrl + maskId})`);
      });
  }

  // this fixes the URL IDs for 'fill'; if you need others, add them here
  fixForAttribute('fill');
}
